import React, { useState } from 'react';
import { Smartphone, Monitor, ArrowRight, Zap, CheckCircle2, Lock, Copy, Send } from 'lucide-react';

export const LiveDemoSimulator: React.FC = () => {
  const [inputText, setInputText] = useState('https://ck-share.pages.dev/join?room=CK-7X9K2M');
  const [pcText, setPcText] = useState('Ctrl + V to paste will show text here...');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'encrypting' | 'routing' | 'synced'>('idle');
  const [latency, setLatency] = useState(12);

  const handleSimulateSync = () => {
    if (!inputText.trim()) return;
    setIsSyncing(true);
    setSyncStatus('encrypting');

    setTimeout(() => {
      setSyncStatus('routing');
      setTimeout(() => {
        setPcText(inputText);
        setSyncStatus('synced');
        setIsSyncing(false);
        setLatency(Math.floor(Math.random() * 8) + 9);
      }, 400);
    }, 300);
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-5 md:p-7 rounded-3xl bg-gradient-to-b from-[#121524]/90 via-[#0d0f1a]/95 to-[#080910] border border-cyan-500/30 backdrop-blur-2xl shadow-[0_20px_60px_rgba(0,0,0,0.8),0_0_30px_rgba(0,240,255,0.15)] relative overflow-hidden">
      
      {/* Background Ambient Flare */}
      <div className="absolute -top-24 -left-24 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -right-24 w-72 h-72 bg-purple-600/20 rounded-full blur-3xl pointer-events-none" />

      {/* Simulator Header */}
      <div className="flex items-center justify-between border-b border-white/10 pb-4 mb-6 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-3 w-3 relative">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-cyan-500"></span>
          </span>
          <h3 className="text-sm font-bold text-white tracking-wide">Interactive Live Sync Simulation</h3>
        </div>

        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1 text-cyan-300 bg-cyan-500/10 px-2.5 py-1 rounded-full border border-cyan-500/30 font-mono">
            <Lock size={12} />
            AES-256-GCM
          </span>
          <span className="flex items-center gap-1 text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-full border border-emerald-500/30 font-mono font-bold">
            <Zap size={12} />
            {syncStatus === 'synced' ? `${latency}ms latency` : 'Cloudflare Edge'}
          </span>
        </div>
      </div>

      {/* Dual Mockup Columns: Phone ➔ PC */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
        
        {/* Left: Simulated Phone */}
        <div className="p-4 rounded-2xl bg-white/[0.03] border border-white/10 relative group">
          <div className="flex items-center justify-between mb-3 text-xs text-gray-400">
            <span className="flex items-center gap-1.5 font-semibold text-emerald-400">
              <Smartphone size={15} />
              Android Phone (WhatsApp/Chrome)
            </span>
            <span className="text-[10px] bg-white/5 px-2 py-0.5 rounded text-gray-400">Step 1: Copy</span>
          </div>

          <div className="space-y-3">
            <div className="relative">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Type any link, token, or text..."
                className="w-full bg-black/60 border border-cyan-500/40 focus:border-cyan-400 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 transition-all font-mono"
              />
            </div>

            <button
              onClick={handleSimulateSync}
              disabled={isSyncing}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs font-bold text-black bg-gradient-to-r from-cyan-400 to-emerald-400 hover:from-cyan-300 hover:to-emerald-300 shadow-[0_0_20px_rgba(0,240,255,0.3)] hover:shadow-[0_0_25px_rgba(0,240,255,0.5)] transition-all disabled:opacity-50"
            >
              {isSyncing ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  <span>{syncStatus === 'encrypting' ? 'Encrypting with AES-256...' : 'Routing via Cloudflare...'}</span>
                </>
              ) : (
                <>
                  <Copy size={14} />
                  <span>Simulate Copy on Phone</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Right: Simulated PC */}
        <div className="p-4 rounded-2xl bg-white/[0.03] border border-white/10 relative">
          <div className="flex items-center justify-between mb-3 text-xs text-gray-400">
            <span className="flex items-center gap-1.5 font-semibold text-purple-400">
              <Monitor size={15} />
              Windows PC (VS Code / Chrome)
            </span>
            <span className="text-[10px] bg-white/5 px-2 py-0.5 rounded text-gray-400">Step 2: Auto Paste</span>
          </div>

          <div className="p-3 rounded-xl bg-black/70 border border-purple-500/30 min-h-[90px] flex flex-col justify-between">
            <div className="font-mono text-xs text-gray-300 break-all select-all">
              {pcText}
            </div>

            <div className="flex items-center justify-between pt-2 mt-2 border-t border-white/5 text-[10px]">
              <span className="text-gray-500">Ready to paste with <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-gray-300 font-bold">Ctrl + V</kbd></span>
              {syncStatus === 'synced' && (
                <span className="text-emerald-400 font-bold flex items-center gap-1 animate-fadeIn">
                  <CheckCircle2 size={11} />
                  Pasted in {latency}ms
                </span>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* Simulator Micro-Flow Explanation */}
      <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between flex-wrap gap-2 text-[11px] text-gray-400">
        <div className="flex items-center gap-1 text-cyan-300">
          <Zap size={13} className="text-cyan-400" />
          <span>Zero-Click: You never have to open the app to copy or paste.</span>
        </div>
        <span className="text-gray-500">Try changing the text above and click "Simulate Copy"!</span>
      </div>

    </div>
  );
};
